import { TestId } from '@trello/webdriverio-tests';
import classNames from 'classnames';
import forwardRefComponent from 'common/lib/util/forward-ref-component';
import React from 'react';
import Icon, { IconColor, IconName, IconProps } from '../icon';
import styles from './button.less';

export enum ButtonTypes {
  DEFAULT = 'default',
  ICON = 'icon',
  PRIMARY = 'primary',
  DANGER = 'danger',
  DISABLED = 'disabled',
  LINKBUTTON = 'linkButton',
}
export enum ButtonSizes {
  DEFAULT = 'default',
  WIDE = 'wide',
  FULLWIDTH = 'fullwidth',
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  size?: ButtonSizes;
  buttonType?: ButtonTypes;
  onClick?: React.MouseEventHandler;
  testId?: TestId;
}

export const Button = forwardRefComponent<HTMLButtonElement, ButtonProps>(
  'Button',
  (
    { children, onClick, className, buttonType, size, testId, ...buttonAttrs },
    ref,
  ) => {
    const { disabled } = buttonAttrs;

    return (
      <button
        ref={ref}
        className={classNames(
          styles.button,
          className,
          styles[buttonType || ButtonTypes.DEFAULT],
          styles[size || ButtonSizes.DEFAULT],
          { [styles[ButtonTypes.DISABLED]]: disabled },
        )}
        onClick={onClick}
        data-test-id={testId}
        {...buttonAttrs}
      >
        {children}
      </button>
    );
  },
);

export const LinkButton = forwardRefComponent<HTMLButtonElement, ButtonProps>(
  'LinkButton',
  ({ children, onClick, className, ...buttonAttrs }, ref) => {
    return (
      <Button
        ref={ref}
        buttonType={ButtonTypes.LINKBUTTON}
        onClick={onClick}
        className={className}
        {...buttonAttrs}
      >
        {children}
      </Button>
    );
  },
);

export interface IconButtonProps extends ButtonProps {
  icon: IconName;
  color: IconColor;
  smallIcon?: boolean;
  largeIcon?: boolean;
  customIcon?: boolean;
}

export const IconButton = forwardRefComponent<
  HTMLButtonElement,
  IconButtonProps
>(
  'IconButton',
  (
    {
      children,
      icon,
      color,
      onClick,
      size,
      largeIcon,
      smallIcon,
      customIcon,
      className,
      ...buttonAttrs
    },
    ref,
  ) => {
    const iconProps: IconProps = {
      name: icon,
      color,
      custom: customIcon,
    };

    if (largeIcon) {
      iconProps.large = true;
      iconProps.className = classNames(
        styles.largeIcon,
        styles[`button-icon-${color || ''}`],
      );
    } else if (smallIcon || (!smallIcon && !largeIcon)) {
      iconProps.small = true;
      iconProps.className = classNames(
        styles.smallIcon,
        styles[`button-icon-${color || ''}`],
      );
    }

    return (
      <Button
        ref={ref}
        size={size}
        onClick={onClick}
        className={classNames(styles.iconButton, className, {
          [styles.iconButtonSmall]: iconProps.small,
          [styles.iconButtonLarge]: iconProps.large,
        })}
        {...buttonAttrs}
      >
        <Icon {...iconProps} />
        {children && (
          <span className={styles.iconButtonContents}>{children}</span>
        )}
      </Button>
    );
  },
);

export const PrimaryButton = forwardRefComponent<
  HTMLButtonElement,
  ButtonProps
>('PrimaryButton', ({ children, onClick, size, ...buttonAttrs }, ref) => {
  return (
    <Button
      ref={ref}
      buttonType={ButtonTypes.PRIMARY}
      size={size}
      onClick={onClick}
      {...buttonAttrs}
    >
      {children}
    </Button>
  );
});

export const DangerButton = forwardRefComponent<HTMLButtonElement, ButtonProps>(
  'DangerButton',
  ({ children, onClick, size, ...buttonAttrs }, ref) => {
    return (
      <Button
        ref={ref}
        buttonType={ButtonTypes.DANGER}
        size={size}
        onClick={onClick}
        {...buttonAttrs}
      >
        {children}
      </Button>
    );
  },
);



// WEBPACK FOOTER //
// ./app/common/components/controls/button/button.tsx